import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Input, Option, Select,
  Sheet, Stack, Switch, Typography,
} from '@mui/joy';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { useAdmin } from '../../AdminContext';
import { APIAdminSettingsList, APIAdminUpdateSetting } from '../../api_admin_settings';

const DEFAULTS = {
  ai_enabled: 'true',
  feed_ranking_engine: 'off',
  ai_profile_moderation_mode: 'advisor',
  ai_ads_moderation_mode: 'advisor',
  ai_model: '',
};
const DESC = {
  ai_enabled: 'Мастер-выключатель ИИ (true/false)',
  feed_ranking_engine: 'Движок ленты: off/heuristic/llm',
  ai_profile_moderation_mode: 'Режим ИИ-модерации анкет: off/advisor/enforcer',
  ai_ads_moderation_mode: 'Режим ИИ-модерации рекламы: off/advisor/enforcer',
  ai_model: 'Модель YandexGPT: пусто=из settings.py, либо ds://<id> для дообученной',
};

const ENGINE_OPTS = [
  { value: 'off', label: 'Выкл (как раньше)' },
  { value: 'heuristic', label: 'Алгоритм (без ИИ)' },
  { value: 'llm', label: 'ИИ (YandexGPT)' },
];
const MODE_OPTS = [
  { value: 'off', label: 'Выкл' },
  { value: 'advisor', label: 'Подсказка' },
  { value: 'enforcer', label: 'Строгий' },
];

export default function AdminSettingsPage() {
  const { adminToken: token } = useAdmin();
  const [vals, setVals] = useState(null);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState(null);
  const [okMsg, setOkMsg] = useState(null);
  const [modelInput, setModelInput] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const items = await APIAdminSettingsList(token);
        const map = { ...DEFAULTS };
        (items || []).forEach((s) => { if (s.key in DEFAULTS) map[s.key] = s.value; });
        if (alive) { setVals(map); setModelInput(map.ai_model || ''); }
      } catch { if (alive) { setVals({ ...DEFAULTS }); setModelInput(''); } }
    })();
    return () => { alive = false; };
  }, [token]);

  const save = async (key, value) => {
    setSaving(key); setError(null); setOkMsg(null);
    const prev = vals[key];
    setVals((m) => ({ ...m, [key]: value }));
    try {
      await APIAdminUpdateSetting(token, key, value, DESC[key]);
      setOkMsg('Сохранено');
      setTimeout(() => setOkMsg(null), 2000);
    } catch {
      setVals((m) => ({ ...m, [key]: prev }));
      setError('Не удалось сохранить (нужна роль superadmin/finance)');
    } finally { setSaving(''); }
  };

  if (!vals) return <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>;

  const aiOn = String(vals.ai_enabled).toLowerCase() !== 'false';
  const dim = { opacity: aiOn ? 1 : 0.55 };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <AutoAwesomeRoundedIcon color="primary" />
        <Typography level="h4">Настройки ИИ</Typography>
      </Stack>
      <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
        Менять значения может роль superadmin/finance. Режимы модерации применяются при следующем прогоне; лента реагирует сразу.
      </Typography>

      {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
      {okMsg && <Alert color="success" sx={{ mb: 2 }}>{okMsg}</Alert>}

      {/* Мастер-выключатель */}
      <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box>
            <Typography level="title-sm">ИИ включён</Typography>
            <Typography level="body-xs" color="neutral">
              Общий выключатель. Выкл — весь ИИ не работает: лента идёт без ИИ, модерация ничего не проверяет.
            </Typography>
          </Box>
          <Switch
            checked={aiOn}
            disabled={saving === 'ai_enabled'}
            onChange={(e) => save('ai_enabled', e.target.checked ? 'true' : 'false')}
          />
        </Stack>
      </Sheet>

      {/* Подбор анкет */}
      <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2, ...dim }}>
        <Typography level="title-sm" sx={{ mb: 1 }}>Подбор анкет (лента)</Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography level="body-sm">Движок ранжирования</Typography>
          <Select size="sm" value={vals.feed_ranking_engine} sx={{ minWidth: 210 }}
            disabled={saving === 'feed_ranking_engine'}
            onChange={(_, v) => v && save('feed_ranking_engine', v)}>
            {ENGINE_OPTS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
          </Select>
        </Stack>
      </Sheet>

      {/* Модерация анкет */}
      <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2, ...dim }}>
        <Typography level="title-sm" sx={{ mb: 1 }}>Модерация анкет</Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography level="body-sm">Режим</Typography>
          <Select size="sm" value={vals.ai_profile_moderation_mode} sx={{ minWidth: 210 }}
            disabled={saving === 'ai_profile_moderation_mode'}
            onChange={(_, v) => v && save('ai_profile_moderation_mode', v)}>
            {MODE_OPTS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
          </Select>
        </Stack>
      </Sheet>

      {/* Модерация рекламы */}
      <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2, ...dim }}>
        <Typography level="title-sm" sx={{ mb: 1 }}>Модерация рекламы</Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography level="body-sm">Режим</Typography>
          <Select size="sm" value={vals.ai_ads_moderation_mode} sx={{ minWidth: 210 }}
            disabled={saving === 'ai_ads_moderation_mode'}
            onChange={(_, v) => v && save('ai_ads_moderation_mode', v)}>
            {MODE_OPTS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
          </Select>
        </Stack>
      </Sheet>

      {/* Модель */}
      <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2, ...dim }}>
        <Typography level="title-sm" sx={{ mb: 0.5 }}>Модель ИИ</Typography>
        <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
          Пусто = из настроек сервера. Базовая: <b>yandexgpt-lite</b> / <b>yandexgpt</b>.
          Дообученная: <b>ds://&lt;id&gt;</b> или <b>gpt://&lt;folder&gt;/&lt;model&gt;/latest</b>.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Input size="sm" placeholder="yandexgpt-lite" value={modelInput}
            onChange={(e) => setModelInput(e.target.value)} sx={{ flex: 1 }} />
          <Button size="sm" loading={saving === 'ai_model'} onClick={() => save('ai_model', modelInput.trim())}>
            Сохранить
          </Button>
        </Stack>
      </Sheet>
    </Box>
  );
}